"use client";
import { useState, useRef } from "react";
import { Icon } from "@iconify/react";
import { InputField, TextareaField, SelectField } from "@/components/Fields";
import ReCAPTCHA from "react-google-recaptcha";

const ContactForm = ({ slice, showText = false }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    enquiryType: "",
    message: "",
  });
  const [statusMessage, setStatusMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const recaptchaRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleDateChange = (e) => {
    setFormData((prevData) => ({
      ...prevData,
      dateOfEvent: e.target.value,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      dateOfEvent: "",
    }));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName) newErrors.fullName = "Full Name is required";
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Email is not valid";
    }
    if (!formData.phone) newErrors.phone = "Phone Number is required";
    if (!formData.enquiryType)
      newErrors.enquiryType = "Type of enquiry is required";
    if (!formData.message) newErrors.message = "Message is required";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatusMessage(""); // Clear previous status message

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setStatusMessage("Please fill in all required fields.");
      setIsSuccess(false);
      return;
    }

    try {
      const token = await recaptchaRef.current.executeAsync();
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...formData, captchaToken: token }),
      });

      if (response.ok) {
        setStatusMessage("Message sent successfully!");
        setIsSuccess(true);
        setFormData({
          fullName: "",
          email: "",
          phone: "",
          enquiryType: "",
          message: "",
        }); // Clear form fields
        recaptchaRef.current.reset(); // Reset reCAPTCHA
      } else {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          setStatusMessage(`Error: ${errorData.message}`);
        } else {
          const errorText = await response.text();
          setStatusMessage(`${errorText}`);
        }
        setIsSuccess(false);
      }
    } catch (error) {
      setStatusMessage("An unexpected error occurred. Please try again later.");
      setIsSuccess(false);
    }
  };

  const enquiryTypes = ["Earwax Removal", "Ear Examination", "Other"];

  return (
    <div
      className={`flex items-end w-5/12 ${showText ? "max-w-7xl flex-col md:flex-row" : "flex-col"}`}
    >
      <form
        onSubmit={handleSubmit}
        noValidate
        className="form-consultation w-full"
      >
        <h4 className="mb-20 text-center">Send us a message</h4>
        <div className="grid grid-cols-1 gap-4 pt-4">
          <InputField
            label="First Name"
            name="fullName"
            placeholder="Enter your full name"
            value={formData.fullName}
            onChange={handleChange}
            required
            error={errors.fullName}
          />

          <InputField
            label="Email"
            name="email"
            type="email"
            placeholder="Enter your email"
            value={formData.email}
            onChange={handleChange}
            required
            error={errors.email}
          />

          <InputField
            label="Phone Number"
            name="phone"
            type="email"
            placeholder="Enter your phone number"
            value={formData.phone}
            onChange={handleChange}
            required={false}
            error={errors.phone}
          />

          <SelectField
            label="Type of enquiry"
            name="enquiryType"
            value={formData.enquiryType}
            options={enquiryTypes}
            onChange={handleChange}
            required
            error={errors.enquiryType}
          />
        </div>
        <div className="mt-4">
          <TextareaField
            label="Message"
            name="message"
            placeholder="Write your message here…"
            value={formData.message}
            onChange={handleChange}
            required
            error={errors.message}
          />
        </div>
        <div className="mt-4">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
            size="invisible"
          />
        </div>
        <div className="btn-get">
          <button
            type="submit"
            className="tf-btn style-default btn-color-secondary pd-40 boder-8 send-wrap"
          >
            <span>
              Send message <Icon icon="mdi:envelope" className="w-6 h-6 ml-2" />
            </span>
          </button>
        </div>
        {statusMessage && (
          <div
            className={`mt-2 text-2xl ${isSuccess ? "text-green-600" : "text-red-600"}`}
          >
            {statusMessage}
          </div>
        )}
      </form>
    </div>
  );
};

export default ContactForm;
